import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { ROUTES } from 'routes/constants';
import { useCanaisComercializacao, CuffaCommercializationChannelEnum } from './useCanaisComercializacao';

export default function CanaisComercializacao() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const {
    formik,
    initialLoading,
    saving,
    snackbar,
    canaisOptions,
    resumoPrioridades,
    handleSelecionar,
    handleOrderChange,
    handleSave,
    handleAdvance,
    setSnackbar,
  } = useCanaisComercializacao();

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Canais de Comercialização" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Canais de Comercialização" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Canais utilizados na comercialização" width="100%">
            <Box mb={3}>
              <Typography fontWeight="bold" mb={1}>
                Assinale todos os canais de comercialização da produção na unidade
                familiar, segundo sua ordem de importância:
              </Typography>
              <Typography fontSize="14px" color="text.secondary">
                A ordem será preenchida automaticamente. Se necessário, você pode ajustá-la.
              </Typography>
            </Box>

            {formik.touched.canaisSelecionados && formik.errors.canaisSelecionados && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formik.errors.canaisSelecionados}
              </Alert>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
              {canaisOptions.map((nome) => (
                <Box key={nome} display="flex" justifyContent="space-between" alignItems="center" sx={{ paddingBottom: 2 }}>
                  <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                    <Checkbox
                      checked={formik.values.canaisSelecionados.includes(nome)}
                      onChange={() => handleSelecionar(nome)}
                    />
                    <Typography>{nome}</Typography>
                  </Box>
                  {formik.values.canaisSelecionados.includes(nome) && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontSize="14px">Ordem:</Typography>
                      <Select
                        size="small"
                        sx={{ width: 80 }}
                        // O valor já vem preenchido automaticamente pela lógica do `handleSelecionar`
                        value={formik.values.ordens.find((o) => o.value === nome)?.order ?? ''}
                        onChange={(e) => handleOrderChange(nome, Number(e.target.value))}
                      >
                        {/* As opções do Select são geradas dinamicamente com base no número de itens selecionados */}
                        {[...Array(formik.values.canaisSelecionados.length)].map((_, i) => (
                          <MenuItem key={i + 1} value={i + 1}>
                            {i + 1}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {resumoPrioridades.length > 0 && (
              <Box mt={3} p={2} sx={{ background: '#eef5ff', borderRadius: 2 }}>
                <Typography fontWeight="bold" mb={1}>Resumo das Prioridades:</Typography>
                {resumoPrioridades.map(item => (
                  <Typography key={item.order} sx={{ lineHeight: 1.7 }}>
                    {item.order}º - {item.label}
                  </Typography>
                ))}
              </Box>
            )}

            {formik.values.canaisSelecionados.includes(CuffaCommercializationChannelEnum.OUTRO) && (
              <Box mt={2}>
                <TextField
                  name="outroTexto"
                  value={formik.values.outroTexto}
                  onChange={(e) => formik.setFieldValue("outroTexto", e.target.value.toUpperCase())}
                  onBlur={formik.handleBlur}
                  placeholder="Descreva o outro canal de comercialização"
                  fullWidth
                  multiline
                  rows={2}
                  error={!!formik.errors.outroTexto && formik.touched.outroTexto}
                  helperText={formik.touched.outroTexto && formik.errors.outroTexto}
                  sx={{ background: '#fff', borderRadius: 1 }}
                />
              </Box>
            )}

            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.SELOS_AGREGACAO_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>
                Retornar
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleAdvance} disabled={saving}>
                {saving && <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />}
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}
